/**
 * e-voque Brand Colors
 * RGB values and hex codes for consistent branding
 */

export const brandColors = {
  // Main corporate color
  deepNavy: {
    rgb: 'rgb(27, 27, 104)',
    hex: '#1B1B68',
    name: 'Deep Navy',
    purpose: 'Main corporate color'
  },
  
  // Primary blue
  primaryBlue: {
    rgb: 'rgb(0, 51, 153)',
    hex: '#003399',
    name: 'Primary Blue',
    purpose: 'Primary brand color'
  },
  
  // Accent electric blue
  accentElectricBlue: {
    rgb: 'rgb(137, 136, 252)',
    hex: '#8988FC',
    name: 'Accent Electric Blue',
    purpose: 'Accent color'
  },
  
  // Accent orange
  accentOrange: {
    rgb: 'rgb(255, 171, 34)',
    hex: '#FFAB22',
    name: 'Accent Orange',
    purpose: 'Accent color'
  },
  
  // Green accent
  greenAccent: {
    rgb: 'rgb(89, 160, 71)',
    hex: '#59A047',
    name: 'Green Accent',
    purpose: 'Accent color'
  },
  
  // Black
  black: {
    rgb: 'rgb(0, 0, 0)',
    hex: '#000000',
    name: 'Black',
    purpose: 'Text and contrast'
  },
  
  // White
  white: {
    rgb: 'rgb(255, 255, 255)',
    hex: '#FFFFFF',
    name: 'White',
    purpose: 'Background and contrast'
  }
} as const;

// Helper function to get color by name
export function getBrandColor(colorName: keyof typeof brandColors): string {
  return brandColors[colorName].hex;
}

// Helper function to get RGB color with opacity (returns rgba string)
export function getBrandColorRgba(colorName: keyof typeof brandColors, opacity: number = 1): string {
  const rgb = brandColors[colorName].rgb;
  return rgb.replace('rgb', 'rgba').replace(')', `, ${opacity})`);
}

// CSS variables for Tailwind (can be used in globals.css)
export const brandColorCSS = `
  :root {
    --color-deep-navy: ${brandColors.deepNavy.hex};
    --color-primary-blue: ${brandColors.primaryBlue.hex};
    --color-accent-electric-blue: ${brandColors.accentElectricBlue.hex};
    --color-accent-orange: ${brandColors.accentOrange.hex};
    --color-green-accent: ${brandColors.greenAccent.hex};
    --color-black: ${brandColors.black.hex};
    --color-white: ${brandColors.white.hex};
  }
`;

// SVG Background Pattern for Benefits Culture Section
// Creates geometric shapes (circles, squares, triangles) connected by lines
export const benefitsCultureSVGBG = (): string => {
  const orange = brandColors.accentOrange.hex;
  const blue = brandColors.primaryBlue.hex;
  const navy = brandColors.deepNavy.hex;
  
  // Create SVG pattern with geometric shapes connected by lines
  const svg = `<svg width="200" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"><defs><pattern id="benefits-pattern" x="0" y="0" width="200" height="200" patternUnits="userSpaceOnUse"><g class="geometric-shapes"><circle cx="30" cy="30" r="8" fill="${orange}" opacity="0.15" class="shape-circle-1"/><rect x="150" y="30" width="16" height="16" fill="${blue}" opacity="0.15" class="shape-square-1" transform="rotate(45 158 38)"/><polygon points="100,20 110,40 90,40" fill="${navy}" opacity="0.15" class="shape-triangle-1"/><circle cx="170" cy="170" r="8" fill="${orange}" opacity="0.15" class="shape-circle-2"/><rect x="30" y="150" width="16" height="16" fill="${blue}" opacity="0.15" class="shape-square-2" transform="rotate(45 38 158)"/><polygon points="100,180 110,160 90,160" fill="${navy}" opacity="0.15" class="shape-triangle-2"/><line x1="30" y1="30" x2="100" y2="20" stroke="${orange}" stroke-width="1.5" opacity="0.2" class="line-1"/><line x1="100" y1="20" x2="150" y2="30" stroke="${blue}" stroke-width="1.5" opacity="0.2" class="line-2"/><line x1="150" y1="30" x2="170" y2="170" stroke="${navy}" stroke-width="1.5" opacity="0.2" class="line-3"/><line x1="170" y1="170" x2="100" y2="180" stroke="${orange}" stroke-width="1.5" opacity="0.2" class="line-4"/><line x1="100" y1="180" x2="30" y2="150" stroke="${blue}" stroke-width="1.5" opacity="0.2" class="line-5"/><line x1="30" y1="150" x2="30" y2="30" stroke="${navy}" stroke-width="1.5" opacity="0.2" class="line-6"/></g></pattern></defs><rect width="100%" height="100%" fill="url(#benefits-pattern)"/></svg>`;
  
  // Encode to data URI (URL encode for browser compatibility)
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

